// @ts-check
import { gql } from '@apollo/client';
import { Image, Link } from '@react-pdf/renderer';
import { Box, Flex, Text, Title, useTheme } from '../../../pdf-design-system';
import AwardIcon from '../../../pdf-design-system/icons/AwardIcon.js';
import ShoppingCartIcon from '../../../pdf-design-system/icons/ShoppingCartIcon.js';
import {
  Provider as ProductProvider,
  useProduct,
} from '../../../pdf-design-system/Product';
import {
  Provider as RegistrationProvider,
  useRegistration,
} from '../../../pdf-design-system/Registration';
import BookmarkIcon from '../icons/BookmarkIcon.js';
import dinLight from '../fonts/300.js';
import dinMedium from '../fonts/500.js';
import HeartIcon from '../icons/HeartIcon.js';
import QRCode from '../components/LogoQRCode';
import useRoom from '../useRoom';
import React from 'react';
import * as exhibitor from '../exhibitor';
import {
  originPrefix,
  nameWithoutOrigin,
  originSuffix,
} from '../helpers/product';

/**
 * Renders a single table.
 *
 * @param {{style?: object}} props
 */
export default function VieVinumMyListTable({ style, ...rest }) {
  /** @type {Stand} */
  const table = useRegistration();
  return (
    <RegistrationProvider value={afterQuery(table)}>
      <Box
        style={[
          {
            paddingHorizontal: '15mm',
            paddingTop: '10mm',
            paddingBottom: '6mm',
            height: '148.5mm',
            display: 'flex',
          },
          style,
        ]}
        wrap={false}
        {...rest}
      >
        <Flex>
          <TableHeader
            style={{ height: '11mm', width: '35mm', marginRight: '6mm' }}
          />
          <ExhibitorHeader style={{ height: '11mm', flexGrow: 1 }} />
        </Flex>
        <Flex style={{ paddingTop: '2.5mm', flexGrow: 1 }}>
          <ExhibitorColumn style={{ width: '35mm', marginRight: '6mm' }} />
          <ProductsColumn style={{ flexGrow: 1 }} />
        </Flex>
        <TableFooter style={{ marginBottom: '10mm', marginTop: '-7mm' }} />
      </Box>
      {/* Hack so React isn't removed from dependencies. */}
      <React.Fragment />
    </RegistrationProvider>
  );
}

function TableHeader({ ...rest }) {
  const { theme } = useTheme();
  /** @type {Table} */
  const table = useRegistration();
  return (
    <Box {...rest}>
      <Flex
        style={{
          height: '100%',
          paddingVertical: '1mm',
          paddingHorizontal: '1.3mm',
          backgroundColor: theme.colors.primary,
          color: theme.colors.onPrimary,
          flexDirection: 'row',
        }}
      >
        <Box style={{ flexGrow: 1, marginTop: 'auto' }}></Box>
        <Box style={{ textTransform: 'uppercase' }}>
          <Text
            style={{
              marginLeft: 'auto',
              marginRight: '0.7mm',
              letterSpacing: '0.5pt',
            }}
          >
            Tisch
          </Text>
          <Title variant="large" style={{ marginLeft: 'auto' }}>
            {table.stand?.number ?? table.stand?.name ?? '---'}
          </Title>
        </Box>
      </Flex>
    </Box>
  );
}

function ExhibitorHeader({ style, ...rest }) {
  const { theme } = useTheme();
  const room = useRoom();
  /** @type {Table & { bookmark?: { type: 'registration' } }} */
  const table = useRegistration();
  const line1 = text(table.nameLine1);
  const line2 = text(table.nameLine2);
  const nameLine1 = exhibitor.nameLine1(table.profile?.name, line1, line2);
  const nameLine2 = exhibitor.nameLine2(table.profile?.name, line1, line2);
  const name = `${nameLine1} ${nameLine2}`.trim();
  return (
    <Flex
      {...rest}
      style={[
        {
          height: '100%',
          paddingBottom: '2mm',
          borderBottom: 1,
          borderBottomColor: theme.colors.primary,
          color: theme.colors.primary,
        },
        style,
      ]}
    >
      <Box
        style={{
          flexGrow: 1,
          marginRight: '1mm',
          marginTop: 'auto',
          maxHeight: '100%', // Make sure it doesn't overflow.
        }}
      >
        <Title variant="small">{room.name}</Title>
        <Title>{name}</Title>
        {table.bookmark && (
          <Bookmark
            style={{
              position: 'absolute',
              left: '-6mm',
              top: 12,
              color: theme.colors.brand,
            }}
          />
        )}
      </Box>
      <HeaderText variant="default.medium" style={{ width: '35mm' }}>
        {table.profile?.region}
      </HeaderText>
      <HeaderText style={{ width: '8mm', color: 'black' }}>ALK</HeaderText>
      <HeaderText style={{ width: '8mm', color: 'black' }}>RZ</HeaderText>
      <HeaderText style={{ width: '8mm', color: 'black' }}>SR</HeaderText>
    </Flex>
  );
}

function HeaderText({ children, style, ...rest }) {
  return (
    <Text
      style={[
        { textAlign: 'right', marginTop: 'auto', marginBottom: -2 },
        style,
      ]}
      {...rest}
    >
      {children}
    </Text>
  );
}

function ExhibitorColumn({ style, ...rest }) {
  /** @type {Table} */
  const table = useRegistration();
  const profile = table.profile || {};
  return (
    <Box style={style} {...rest}>
      <Box>
        {profile?.autoSizedLogo?.fixed?.src && (
          <Image
            src={profile.autoSizedLogo.fixed.src}
            style={{ maxHeight: '40mm', marginHorizontal: 'auto' }}
          />
        )}
      </Box>
      <Box style={{ marginTop: '2mm', marginBottom: '3mm' }}>
        {table.companyId && (
          <>
            {table.siteUrl ? (
              <QRCode
                // url={`HTTP://VIN.TO/f3422`}
                url={`${table.siteUrl}/e/${table.companyId}`
                  // Uppercasing the URL makes it simpler to encode, resulting in less complexity.
                  .toUpperCase()}
                style={{
                  width: '15.5mm',
                  height: '15.5mm',
                  marginBottom: '2mm',
                }}
                margin={0}
                logo="unserwein"
              />
            ) : (
              <QRCode
                url={`http://app.bottlebooks.me/`}
                style={{
                  width: '15.5mm',
                  height: '15.5mm',
                  marginBottom: '2mm',
                }}
                margin={0}
                logo="unserwein"
              />
            )}
            {table.hatPremiumeintrag && (
              <>
                <Text variant="default.medium" style={{ marginBottom: 0 }}>
                  Premiumeintrag
                </Text>
                <Text style={{ marginBottom: 0 }}>
                  {premiumeintragText(table)}
                </Text>
              </>
            )}
            <Text>scannen &amp; bewerten</Text>
          </>
        )}
      </Box>
      <Box style={{ marginBottom: '3mm' }}>
        <Text>{profile?.street}</Text>
        <Text>
          {profile?.postalCode} {profile?.city}
        </Text>
        {profile?.country !== 'Österreich' && <Text>{profile?.country}</Text>}
      </Box>
      <Box style={{ marginBottom: '3mm' }}>
        <Text>{profile?.telephone}</Text>
        <Text>{profile?.email}</Text>
        <Text>{profile?.websiteText}</Text>
        <Text>{text(table?.websites)}</Text>
      </Box>
      <Zertifizierungslogos style={{ marginBottom: '3mm' }} />
      <Text style={{ marginBottom: '3mm' }}>{profile.associations}</Text>
    </Box>
  );
}

function Zertifizierungslogos({ ...rest }) {
  /** @type {Table} */
  const table = useRegistration();
  if (!table.logos?.length) return null;
  return (
    <Flex {...rest}>
      {table.logos?.map((key, index) => (
        <Image
          key={`${key}-${index}`}
          // 7.5mm in 300dpi (7.5/2.54*300).
          src={`https://res.cloudinary.com/bottlebooks/image/upload/c_pad,h_89,q_auto,w_89/v1/vievinum/${key}.jpg`}
          style={{
            marginRight: '1mm',
            width: '7.5mm',
            height: '7.5mm',
          }}
        />
      ))}
    </Flex>
  );
}

function ProductsColumn({ style, ...rest }) {
  /** @type {Table} */
  const { registeredProducts } = useRegistration();
  return (
    <Box
      style={[{ display: 'flex', justifyContent: 'space-between' }, style]}
      {...rest}
    >
      {registeredProducts.map((product) => (
        <ProductProvider key={product.productId} value={product}>
          <Product style={{ minHeight: '15mm' }} />
        </ProductProvider>
      ))}
      <ProductProvider value={{}}>
        {Array.from(Array(Math.max(0, 6 - registeredProducts.length))).map(
          (_, index) => (
            <Product key={index} style={{ minHeight: '15mm' }} />
          )
        )}
      </ProductProvider>
    </Box>
  );
}

function Product({ style, ...rest }) {
  /** @type {Table["registeredProducts"][number] & { bookmark?: {type: 'product' }; tastingNote?: { rating?: 0 | 1 | 2 | 3 | 4 | 5; note?: string; } }} */
  const { product, bookmark, tastingNote, ...custom } = useProduct();
  /** @type {Table} */
  const { profile } = useRegistration();
  const { theme } = useTheme();
  return (
    <Box style={style} {...rest}>
      <Flex>
        <Box>
          {bookmark && (
            <Bookmark
              style={{
                position: 'absolute',
                left: '-6mm',
                color: theme.colors.brand,
              }}
            />
          )}
          {product?.shortName ? (
            <>
              <Box style={{ display: 'flex', flexDirection: 'row' }}>
                <Box>
                  {product.producer?.name &&
                  product.producer?.name !== profile?.name ? (
                    <Text variant="large">{product.producer?.name}</Text>
                  ) : null}
                  <Text variant="large.medium">{product?.prefix}</Text>
                  <Box style={{ display: 'flex', flexDirection: 'row' }}>
                    <Text variant="large.medium">
                      {product?.name}
                      {product?.__typename === 'Wine' && (
                        <Text variant="large"> {product?.vintage}</Text>
                      )}
                    </Text>
                    {product?.onlineShopUrl && (
                      <Link
                        src={product.onlineShopUrl}
                        style={{ marginLeft: '1mm', paddingTop: 1 }}
                      >
                        <ShoppingCartIcon color="#555" size={8} />
                      </Link>
                    )}
                  </Box>
                  <Text variant="large.medium" style={{ marginBottom: 1 }}>
                    {product?.suffix}
                  </Text>
                </Box>
              </Box>
              {product?.__typename === 'Wine' && (
                <>
                  <Text>
                    {product?.grapeVarieties
                      ?.map(({ varietyName, percentage }) =>
                        percentage
                          ? `${percentage}% ${varietyName}`
                          : varietyName
                      )
                      .join(', ')}
                  </Text>
                </>
              )}
              <Text>{text(custom.annotation)}</Text>
              {product?.awards?.map((award, index) => (
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                  key={index}
                >
                  <AwardIcon
                    color="#000"
                    size="9pt"
                    style={{ marginHorizontal: -1 }}
                  />
                  <Text style={{ marginLeft: '1mm' }}>
                    {`${award.medal || ''} ${award.award || ''}`.trim()}
                  </Text>
                </Box>
              ))}
            </>
          ) : null}
        </Box>
        {tastingNote?.note ? (
          <Text
            variant="small"
            style={{
              marginTop: 11,
              flex: '1 1 0',
              marginLeft: '3mm',
              textAlign: 'right',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {tastingNote.note}
          </Text>
        ) : (
          <Box style={{ flexGrow: 1 }} />
        )}
        <Text style={{ width: '8mm', textAlign: 'right' }}>
          {formatNumber(product?.alcohol)}
        </Text>
        <Text style={{ width: '8mm', textAlign: 'right' }}>
          {formatNumber(product?.residualSugar)}
        </Text>
        <Text style={{ width: '8mm', textAlign: 'right' }}>
          {formatNumber(product?.totalAcidity)}
        </Text>
      </Flex>
      <Flex style={{ position: 'absolute', top: '3.2mm', right: '-0.5mm' }}>
        <HeartIcon
          variant={tastingNote?.rating > 0 ? 'full' : 'empty'}
          color={tastingNote?.rating > 0 ? theme.colors.brand : '#aaa'}
          style={{ width: '4mm', marginLeft: '0.3mm' }}
        />
        <HeartIcon
          variant={tastingNote?.rating > 1 ? 'full' : 'empty'}
          color={tastingNote?.rating > 1 ? theme.colors.brand : '#aaa'}
          style={{ width: '4mm', marginLeft: '0.3mm' }}
        />
        <HeartIcon
          variant={tastingNote?.rating > 2 ? 'full' : 'empty'}
          color={tastingNote?.rating > 2 ? theme.colors.brand : '#aaa'}
          style={{ width: '4mm', marginLeft: '0.3mm' }}
        />
        <HeartIcon
          variant={tastingNote?.rating > 3 ? 'full' : 'empty'}
          color={tastingNote?.rating > 3 ? theme.colors.brand : '#aaa'}
          style={{ width: '4mm', marginLeft: '0.3mm' }}
        />
        <HeartIcon
          variant={tastingNote?.rating > 4 ? 'full' : 'empty'}
          color={tastingNote?.rating > 4 ? theme.colors.brand : '#aaa'}
          style={{ width: '4mm', marginLeft: '0.3mm' }}
        />
      </Flex>
    </Box>
  );
}

function Bookmark({ style }) {
  return (
    <BookmarkIcon
      size="xsmall"
      style={{ width: '6mm', height: '6mm', marginTop: '-0.5mm', ...style }}
    />
  );
}

function TableFooter({ style, ...rest }) {
  /** @type {Table} */
  const table = useRegistration();
  const profile = table.profile || {};
  if (!profile.onlineShopUrl) return null;
  return (
    <Link
      src={profile.onlineShopUrl}
      style={[{ textDecoration: 'none', flexDirection: 'row' }, style]}
      {...rest}
    >
      <ShoppingCartIcon color="#000" size={8} style={{ marginRight: 3 }} />
      <Text style={{ color: '#000' }}>{profile.onlineShopText}</Text>
    </Link>
  );
}

/**
 * @param {number} number
 */
function formatNumber(number) {
  if (number == null) return '';
  return number.toFixed(1).replace('.', ',');
}

/** @type {import('../../../pdf-design-system/useTheme').BottlebooksPDFTheme["theme"]} */
export const theme = {
  colors: { primary: '#999', onPrimary: 'white', brand: '#c8234a' },
  components: {
    Text: {
      base: { ...dinLight },
      small: { fontSize: 6, lineHeight: 7 / 6 },
      default: { fontSize: 7, lineHeight: 9 / 7 },
      'default.medium': { ...dinMedium, fontSize: 7, lineHeight: 9 / 7 },
      large: { fontSize: 8, lineHeight: 10 / 8 },
      'large.medium': { ...dinMedium, fontSize: 8, lineHeight: 10 / 8 },
      conver: { fontSize: 12, lineHeight: 14 / 12 },
    },
    Title: {
      base: { ...dinMedium, textTransform: 'uppercase' },
      small: { ...dinLight, fontSize: 10, lineHeight: 12 / 10 },
      default: { fontSize: 12, lineHeight: 12 / 12 },
      large: { fontSize: 18, lineHeight: 24 / 18 },
      cover: { ...dinLight, fontSize: 36, lineHeight: 36 / 36 },
      coverSmall: { ...dinLight, fontSize: 18, lineHeight: 24 / 18 },
    },
  },
};

const parse = gql;
/** @type  {import('@apollo/client').TypedDocumentNode<import('./@types/VieVinumMyListTable').VieVinumMyListTableFragment>}*/
VieVinumMyListTable.fragment = parse/* GraphQL */ `
  fragment VieVinumMyListTable on RegistrationCatalogSection {
    registration {
      collection {
        site {
          publicUrl
        }
        # IDs for apollo-client:
        ... on Event {
          eventId
        }
      }
      registrationId
      companyId
      stand {
        name
      }
      exhibitorPackage: customFieldValue(key: "exhibitorPackage") {
        ...VieVinumMyListTable_SelectFieldValue
      }
      zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
        ...VieVinumMyListTable_MultiSelectFieldValue
      }
      nameLine1: customFieldValue(key: "nameLine1") {
        ...VieVinumMyListTable_TextFieldValue
      }
      nameLine2: customFieldValue(key: "nameLine2") {
        ...VieVinumMyListTable_TextFieldValue
      }
      websites: customFieldValue(key: "websites") {
        ...VieVinumMyListTable_TextFieldValue
      }
      profile {
        name
        street
        postalCode
        city
        country
        telephone
        email(format: TEXT)
        websiteText: website(format: TEXT)
        websiteUrl: website(format: URL)
        region
        associations
        offeringTypes
        # Certifications are used as a backup for zertifizierungslogos.
        certifications(format: RAW)
        autoSizedLogo: logo {
          # 33mmx33mm in 300dpi
          fixed(
            width: 392
            crop: PAD
            gravity: NORTH
            fetch_format: JPG
            quality: BEST
          ) {
            src
          }
        }
        onlineShopUrl: onlineShop(format: URL)
        onlineShopText: onlineShop(format: TEXT)
      }
      registeredProducts {
        nodes {
          annotation: customFieldValue(key: "annotation") {
            ...VieVinumMyListTable_TextFieldValue
          }
          productId
          product {
            __typename
            shortName
            countryCode: country(format: RAW)
            country
            denomination
            alcohol
            totalAcidity
            residualSugar
            awards {
              award
              medal
            }
            producer {
              producerId
              name
            }
            onlineShopUrl: onlineShop(format: URL)
            ... on Wine {
              classification
              specialClassification
              vintage
              grapeVarieties {
                varietyName
                percentage
              }
              # For Sekt Austria:
              region
              vineyard
            }
            productId # needed for apollo-client
          }
        }
      }
    }
  }
  fragment VieVinumMyListTable_TextFieldValue on FieldValue {
    __typename
    ... on TextFieldValue {
      value
    }
  }
  fragment VieVinumMyListTable_SelectFieldValue on FieldValue {
    __typename
    ... on SelectFieldValue {
      key
    }
  }
  fragment VieVinumMyListTable_MultiSelectFieldValue on FieldValue {
    __typename
    ... on MultiSelectFieldValue {
      values {
        key
      }
    }
  }
`;

/** @typedef {import('./VieVinumMyList').Stand} Stand */

/** @typedef {Stand["registeredProducts"][number]} RegisteredProduct */

/** @typedef {ReturnType<afterQuery>} Table */
/** @param {Stand} stand */
function afterQuery(stand) {
  const registration = stand.registration;
  const hatPremiumeintrag = hasPremiumeintrag(registration);
  const siteUrl = registration.collection?.site?.publicUrl?.replace(/\/$/, '');
  const profile = registration.profile
    ? {
        ...registration.profile,
        onlineShopText: hatPremiumeintrag
          ? registration.profile.onlineShopText
          : null,
        onlineShopUrl: hatPremiumeintrag
          ? registration.profile.onlineShopUrl
          : null,
      }
    : registration.profile;
  return {
    ...registration,
    logos: zertifizierungslogos(
      registration.zertifizierungslogos,
      // @ts-ignore certifications aren't defined in the model yet.
      registration.profile?.certifications
    ),
    hatPremiumeintrag,
    profile,
    registeredProducts: stand.registeredProducts?.map((registeredProduct) =>
      product(registeredProduct, hatPremiumeintrag)
    ),
    siteUrl,
  };
}

/**
 * @param {RegisteredProduct} registeredProduct
 * @param {boolean} hatPremiumeintrag
 */
function product(registeredProduct, hatPremiumeintrag) {
  const { product } = registeredProduct;
  // @ts-ignore TODO can't type this correctly.
  const prefix = originPrefix(product);
  // @ts-ignore TODO can't type this correctly.
  const name = nameWithoutOrigin(product);
  // @ts-ignore TODO can't type this correctly.
  const suffix = originSuffix(product);
  return {
    ...registeredProduct,
    product: {
      ...registeredProduct.product,
      prefix: prefix === name ? '' : prefix,
      name: name,
      suffix: suffix,
      // Remove premium data from non-premium products.
      awards: hatPremiumeintrag ? product.awards : null,
      onlineShopUrl: hatPremiumeintrag ? product.onlineShopUrl : null,
    },
  };
}

/** @returns {string} */
function text(customField) {
  return customField?.value || '';
}
/**
 * @param {Stand["registration"]["zertifizierungslogos"]} zertifizierungslogos
 * @param {string[] | null} certifications
 * @returns {('bio' | 'demeter' | 'sektAustria' | 'nachhaltigAustria')[] | undefined}
 */
function zertifizierungslogos(zertifizierungslogos, certifications) {
  if (zertifizierungslogos?.__typename === 'MultiSelectFieldValue') {
    if (zertifizierungslogos?.values?.length) {
      // @ts-ignore key is one of the four possible values above.
      return zertifizierungslogos?.values?.map(({ key }) => key);
    }
  }
  return certifications
    ?.map((certification) => certificationMapping[certification])
    .filter(Boolean);
}
const certificationMapping = {
  certifiedOrganicEU: 'bio',
  nachhaltig_austria: 'nachhaltigAustria',
  osterreichischer_sekt: 'sektAustria',
  demeter: 'demeter',
};

/**
 * @param {Stand["registration"]} table
 */
function hasPremiumeintrag(table) {
  if (table.exhibitorPackage?.__typename === 'SelectFieldValue') {
    if (table.exhibitorPackage?.key === 'premium') return true;
    if (table.exhibitorPackage?.key === 'pavilion') return true;
  }
  return false;
}

/** @param {Table} table */
function premiumeintragText(table) {
  if (!table.hatPremiumeintrag) return '';
  const productsWithAwards = table.registeredProducts.filter(
    // @ts-ignore awards are arrays in any case
    ({ product }) => product?.awards?.filter(({ award }) => award).length > 0
  );
  const types = [
    table.profile.offeringTypes?.includes('onlineShop') && 'Shop',
    table.profile.offeringTypes?.includes('videos') && 'Videos',
    table.profile.offeringTypes?.includes('imageGallery') && 'Bildergalerie',
    productsWithAwards?.length && 'Auszeichnungen',
  ].filter(Boolean);
  if (types.length === 0) return 'Mit Zusatzinfo online';
  if (types.length === 1) return `Mit ${types[0]}`;
  return `Mit ${types.slice(0, -1).join(', ')} und ${types[types.length - 1]}`;
}
